import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { COMMON_PROPS } from '@/trendData/baseItem.constants';
import { findChildren, findItemByProp, findItemIndex } from '@/trendData/itemStore.utilities';
import { clearLastRemovedColor, removeColor, updateColorUsage } from '@/trendData/itemStoreColor.utilities';
import { CHILD_CLONED_PROPERTIES, ITEM_CUSTOMIZATIONS, ITEM_TYPES, PREVIEW_ID } from '@/trendData/trendData.constants';
import type { Cursor } from 'baobab';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import type { WithProperty } from '@/utilities.types';

/**
 * A base store that adds standard functionality for all stores that represent an item that can show up on the
 * worksheet.
 */
export type Item = Record<string, any> | any;

interface BaseItemState {
  items: unknown[];
}

export abstract class BaseItemStore<T = Item> extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  /**
   * TODO CRAB-29756: Should check if child class has an initialize function, then call it and check for required
   * properties.
   * @param initializeMode
   */
  initialize(initializeMode?: InitializeMode): void {
    if (this.state) {
      _.forEach(this.state.get('items'), (item) => removeColor(item.color));
      clearLastRemovedColor();
    }
    const initialState: BaseItemState = COMMON_PROPS;
    this.state = this.immutable(initialState);
  }

  get baseHandlers(): typeof this.handlers {
    return this.handlers;
  }

  get items(): T[] {
    return this.state.get('items');
  }

  /**
   * Find an item by ID
   *
   * @param {String} id The ID of the item to find
   * @returns {Object} The item; or undefined if the item could not be found
   */
  findItem = (id: string | number): Item => {
    return this.findItemByProp('id', id);
  };

  /**
   * Find an item by the value of a property on the item
   *
   * @param {string} prop The property name
   * @param {string | number} val The property value
   * @returns {Object} The item; or undefined if the item could not be found
   */
  findItemByProp(prop: string, val: string | number): Item {
    const item = findItemByProp(this.state.get('items'), prop, val);

    if (_.isUndefined(item) && !_.isEmpty(this.state.get('previewChartItem'))) {
      if (_.isArray(this.state.get('previewChartItem'))) {
        return _.find(this.state.get('previewChartItem'), (item) => item[prop] === val);
      } else {
        return this.state.get('previewChartItem')[prop] === val ? this.state.get('previewChartItem') : item;
      }
    }
    return item;
  }

  /**
   * Get an item's cursor by ID. NOTE: the dynamic cursor method "select('items', { id: id })" is specifically
   * not used because it creates listeners that are extra overhead and can lead to memory leaks if not explicitly
   * released.
   *
   * @param {String} id - The ID of the item to find
   * @returns {Object} The cursor to the item
   */
  getItemCursor(id: string): Cursor {
    return this.isPreviewItem(id)
      ? this.state.select('previewChartItem')
      : this.state.select('items', this.findItemIndex(id));
  }

  findItemIndex(id: string): number {
    return findItemIndex(this.state.get('items'), id);
  }

  findChildren(id: string | number): Item[] {
    return findChildren(this.state.get('items'), id);
  }

  /**
   * Determines if there is a preview series and id is preview or this is the object being updated
   *
   * @param {String} id - The ID of the item to test
   * @returns {boolean} - True if the item is the preview item or the object being updated
   */
  isPreviewItem(id: string): boolean {
    return (
      !_.isEmpty(this.state.get('previewChartItem')) &&
      (_.startsWith(id, PREVIEW_ID) || _.endsWith(this.state.get('previewChartItem').id, id))
    );
  }

  /**
   * Sets the status of the item specified by the payload id to the value specified by the status parameter and
   * sets the statusMessage.
   *
   * @param {String} status - one of ITEM_DATA_STATUS, intended to be partially applied
   * @param {Object} payload - object container for arguments
   * @param {String} payload.id - guid of item
   * @param {String} [payload.message] - an optional error message to set, if none is specified the statusMessage
   *                                     is set to ''
   * @param {Number} [payload.warningCount] - an optional count of warnings returned by the backend
   * @param {Object[]} [payload.warningLogs] - an optional array of warning details returned by the backend. Note
   *                                           that warningLogs.length does not always equal warningCount
   * @param {String} [payload.timingInformation] - String providing information for timing display in the
   * requests details panel
   * @param {String} [payload.meterInformation] - String providing information for Data Read display in the
   * requests details panel
   * @param {Boolean} [payload.isSharedRequest] - Boolean flag indicating whether the item's timing and meter
   * information counts are totaled across multiple items
   */
  /**
   * Discards the item's data on terminal statuses so stale data from a previous request is not
   * displayed; the request status itself lives in the item runtime store.
   */
  clearDataForTerminalStatus(payload: { id: string }): void {
    const index = this.findItemIndex(payload.id);
    if (index > -1 && _.has(this.state.get(['items', index]), 'data')) {
      this.state.set(['items', index, 'data'], []);
    }
  }

  /**
   * This function should be overridden if the store needs to change the behavior of TREND_SET_CUSTOMIZATIONS
   *
   * Called when the customizations for an item change, should set the customizations on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} customizations - customize properties being set
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetCustomizations(item: { id: string }, customizations: Record<string, unknown>): void {
    this.getItemCursor(item.id).merge(customizations);
  }

  /**
   * Sets a property on an item if it finds it, but only if the property actually changed.
   *
   * @param {String} id The id of the item.
   * @param {String} property The property to set.
   * @param {*} value The value to set.
   */
  setProperty(id: string, property: string, value: unknown): void {
    const cursor = this.getItemCursor(id);
    if (cursor.exists() && cursor.get(property) !== value) {
      cursor.set(property, value);
    }
  }

  /**
   * This function should be overridden if the store needs to change the behavior of TREND_SET_COLOR
   *
   * Called when the color for an item change, should set the color on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} color - color that should be set
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetColor(item: { id: string } | undefined, color: string, parentId?: string): void {
    if (item) {
      this.getItemCursor(item.id).set('color', color);
    }
  }

  /**
   * This function should be overridden if the store needs to color its chart items regardless of whether its actual
   * items are present in the store
   */
  protected getChartChildrenToColor(): Item[] {
    return [] as Item[];
  }

  /**
   * This function should be overridden if the store needs to change the behavior of TREND_SET_SELECTED
   *
   * Called when the selection for an item change, should set the color on the item as necessary
   *
   * @param item - serialized item object
   * @param selected - selection status
   * @param [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetSelected(item: { id: string }, selected: boolean, parentId?: string): void {
    this.getItemCursor(item.id).set('selected', selected);
  }

  setSelected(payload: { item: { id: string }; selected: boolean }): void {
    const item = this.findItem(payload.item.id);
    if (item) {
      this.onSetSelected(item, payload.selected);
    }

    // Handle propagating selection to children
    _.forEach(this.findChildren(payload.item.id), (item) => {
      if (_.includes(CHILD_CLONED_PROPERTIES[item.childType as keyof typeof CHILD_CLONED_PROPERTIES], 'selected')) {
        this.onSetSelected(item, payload.selected, payload.item.id);
      }
    });
  }

  /**
   * Removes items.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Item[]} payload.items - An array of items to remove
   */
  removeItems(payload: { items: Item[] }): void {
    let mutableItems: Item[];
    const items = _.chain(payload.items)
      .map('id')
      .map((id) => this.findItem(id))
      .compact()
      .value();

    if (items.length) {
      // For performance reasons we acquire a writable items array, mutate it, and then set it back
      mutableItems = this.state.deepClone('items');
      _.forEach(items, (item) => {
        const index = _.findIndex(mutableItems, ['id', item.id]);
        if (index >= 0) {
          mutableItems.splice(index, 1);
          removeColor(item.color);
        }
      });

      this.state.set('items', mutableItems);
    }
  }

  setProperties(payload: WithProperty<{ id: string; includeChildren?: boolean }>): void {
    const cursor = this.getItemCursor(payload.id);
    if (cursor.exists()) {
      _.forEach(_.omit(payload, ['id', 'includeChildren']), (value, key) => {
        if (_.isNil(value)) {
          cursor.unset(key);
        } else {
          cursor.set(key, value);
        }
      });
    }
  }

  protected readonly handlers = {
    TREND_REMOVE_ITEMS: this.removeItems,

    /**
     * Sets property values of an existing item
     *
     * @param payload {Object} Object that has an id property for the item to be updated
     *   along with any other properties that should be updated.
     * @param payload.includeChildren? - If true the property on all children of the item will also be updated
     */
    TREND_SET_PROPERTIES: (payload: { id: string; includeChildren?: boolean }) => {
      this.setProperties(payload);
      if (payload.includeChildren) {
        // Set properties for all children (i.e. for a metric)
        _.forEach(this.findChildren(payload.id), (item) => {
          this.setProperties({ ...payload, id: item.id });
        });
      }
    },

    /**
     * Sets customizations as property values of an existing item
     *
     * @param itemsPayload.items {Object[]} Objects that have an id property for the item to be updated
     *   along with any other properties that should be updated.
     */
    TREND_SET_CUSTOMIZATIONS: (itemsPayload: {
      items: {
        id: string;
        lane: string;
        axisAlign?: string;
        yAxisType?: string;
        itemType?: ITEM_TYPES;
        overlay?: number;
      }[];
    }) => {
      _.forEach(itemsPayload.items, (payload) => {
        const item = this.findItem(payload.id);
        if (item) {
          const customizations = _.pick(payload, [
            ...ITEM_CUSTOMIZATIONS[item.itemType as keyof typeof ITEM_CUSTOMIZATIONS],
          ]);
          this.onSetCustomizations(item, customizations);
        }

        let childPayload = payload;
        let omitProps = ['itemType'];
        if (payload.itemType === ITEM_TYPES.CONDITION) {
          omitProps = _.concat(omitProps, ['lane', 'axisAlign']);
        }

        childPayload = _.omit(childPayload, omitProps);

        if (_.size(childPayload) === 1) {
          return;
        }

        // Handle propagating cloned customizations to children
        // NOTE: children and parents can exist in different stores; the parent may not exist in the child's store
        _.forEach(this.findChildren(payload.id), (item) => {
          const customizations = _.pick(
            childPayload,
            _.intersection(
              ITEM_CUSTOMIZATIONS[item.itemType as keyof typeof ITEM_CUSTOMIZATIONS] as string[],
              CHILD_CLONED_PROPERTIES[item.childType as keyof typeof CHILD_CLONED_PROPERTIES] as string[],
            ),
          );

          if (!_.isEmpty(customizations)) {
            this.onSetCustomizations(item, customizations);
          }
        });
      });
    },

    /**
     * Sets the progress for a request.
     * Only set the meterInformation and timingInformation if the payload provides them to avoid
     * unintended clearing on cancellation!
     *
     * @param {Object} payload - Object container for arguments
     * @param {Number} payload.id - Id of the item
     * @param {Object} payload.progress - the progress percentage for item requests
     * @param {String} [payload.timingInformation] - String providing information for timing display in the
     * requests details panel
     * @param {String} [payload.meterInformation] - String providing information for Data Read display in the
     * requests details panel
     */
    /**
     * Set the color on an item.
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.id - The id of the item to change
     * @param {String} payload.color - The color
     */
    TREND_SET_COLOR: (payload: { id: string; color: string }) => {
      const item = this.findItem(payload.id);
      if (item) {
        removeColor(item.color);
        this.onSetColor(item, payload.color);
        updateColorUsage(payload.color);
      }

      // Handle propagating colors to children
      const children = this.findChildren(payload.id);
      if (children.length > 0) {
        _.forEach(children, (item) => {
          if (_.includes(CHILD_CLONED_PROPERTIES[item.childType as keyof typeof CHILD_CLONED_PROPERTIES], 'color')) {
            this.onSetColor(item, payload.color, payload.id);
          }
        });
      } else {
        // Handle propagating color to chart children
        if (this.getChartChildrenToColor().length > 0) {
          this.onSetColor(undefined, payload.color, payload.id);
        }
      }
    },

    /**
     * Set the selected property for multiple capsules
     * @param payload - Object container for arguments
     * @param payload.capsules - an array of capsules.
     * @param payload.selected - Selection status
     */
    TREND_SET_SELECTED_CAPSULES: (payload: { capsules: Item[]; selected: boolean }) => {
      _.forEach(payload.capsules, (capsule) => {
        this.setSelected({ item: capsule, selected: payload.selected });
      });
    },

    /**
     * Set the selected property on an item.
     *
     * @param {Object} payload - Object container for arguments
     * @param {Object} payload.item - The item to select
     * @param {boolean} payload.selected - Selection status
     */
    TREND_SET_SELECTED: this.setSelected,

    /**
     * Swaps out the specified items from one asset for the variants based off another asset.
     *
     * @param {Object} payload - Object container for arguments
     * @param {Object} payload.swaps - The items that were swapped where the keys are the swapped out ids and the
     *   values are the corresponding swapped in ids.
     * @param {Object} payload.outAsset - Asset that was swapped out
     * @param {String} payload.outAsset.id - The ID of the asset to swapped out
     * @param {String} payload.inAsset.name - The name of the asset that was swapped out
     * @param {Object} payload.inAsset - Asset that was swapped in
     * @param {String} payload.inAsset.id - The ID of the asset that was swapped in
     * @param {String} payload.inAsset.name - The name of the asset that was swapped in
     */
    TREND_SWAP_ITEMS: (payload: { swaps: Record<string, string> }) => {
      const replacements = Object.entries(payload.swaps).map(([source, destination]) => ({
        cursor: this.getItemCursor(source),
        destination,
      }));
      replacements.forEach(({ cursor, destination }) => {
        if (cursor.exists()) cursor.set('id', destination);
      });
    },

    TREND_SET_DATA_STATUS_FAILURE: this.clearDataForTerminalStatus,
    TREND_SET_DATA_STATUS_REDACTED: this.clearDataForTerminalStatus,
    TREND_SET_DATA_STATUS_HIDDEN_FROM_TREND: this.clearDataForTerminalStatus,
    TREND_SET_DATA_STATUS_NOT_REQUIRED: this.clearDataForTerminalStatus,
    TREND_SET_DATA_STATUS_ABORTED: this.clearDataForTerminalStatus,
  };
}
